import React, { FunctionComponent, useState, useEffect, Fragment } from 'react';
import Card from '../../components/Card/Card';
import { ToggleSwitch } from '../../design-system';
import { StoreProviderValue, useStore } from '../../store/storeContext';
import { HeaderProviderValue, useHeader } from '../../components/Header/HeaderContext';
import useUpdateEffect from '../../hooks/UseUpdateEffect';
import {
    useNotification,
    NotificationProviderValue,
} from '../../components/Notification/NotificationBarContext';
import useConfirmUnsavedChanges from '../../hooks/useConfirmUnsavedChanges';
import ConfirmUnsavedChanges from '../../components/ConfirmUnsavedChanges/ConfirmUnsavedChanges';
import { APIPlaygroundItemKeyForSidenav, SideNavActiveItemStoreKey } from '../ViewConstant';
import { H4, SubHeading } from '../../design-system/Headings/Headings';
import { utilityFunctions } from '../../utilities/functions';
import NotificationBar from '../../components/Notification/NotificationBar';
import SaveAction from '../../components/SaveAction/SaveAction';
import { UiStateDef } from '../../types/ui-state';
import Tooltip from '../../design-system/Tooltip/Tooltip';
import Spinner from '../../design-system/Spinner/spinner';
import {
    ApiPlaygroundOptionsResponseDef,
    getApiPlaygroundOptions,
    updateApiPlaygroundOptions,
} from '../../api-client/ApiPlaygroundOptionsService';
import { apiPlaygroundStoreKey } from './ApiPlaygroundConstant';

const ApiPlaygroundView: FunctionComponent = () => {
    const { addNotification } = useNotification() as NotificationProviderValue;
    const {
        updateStore,
        updateStoreApiEntity,
        storeState: { activeApiEntityId, apiEntities },
    } = useStore() as StoreProviderValue;
    const { headerState, resetHeaderState } = useHeader() as HeaderProviderValue;
    const [useLegacyApiPlayground, setUseLegacyApiPlayground] = useState(
        apiEntities[activeApiEntityId]?.apiPlaygroundOptions?.useLegacyApiPlayground || false
    );

    const [uiState, setUiState] = useState<UiStateDef>({
        isLoading: true,
        isError: false,
        isSaving: true,
        statusCode: 0,
        message: '',
    });

    const [showModal, setShowModal] = useState(false);
    const [discard, setDiscard] = useState(false);

    useEffect(() => {
        updateStore(APIPlaygroundItemKeyForSidenav, SideNavActiveItemStoreKey);
    }, []);

    useEffect(() => {
        setUiState(utilityFunctions.getLoadingUiState());
        const cacheData =
            Object.keys(apiEntities).includes(activeApiEntityId) &&
            apiEntities[activeApiEntityId].apiPlaygroundOptions;

        if (cacheData) {
            setUiState(utilityFunctions.getDefaultUiState());
            setUseLegacyApiPlayground(cacheData.useLegacyApiPlayground);
            return;
        }

        const promise = getApiPlaygroundOptions(activeApiEntityId);
        promise
            .then((resp: ApiPlaygroundOptionsResponseDef) => {
                setUiState(utilityFunctions.getDefaultUiState());
                const useLegacyApiPlayground =
                    resp.apiPlaygroundOptions?.useLegacyApiPlayground ?? false;

                setUseLegacyApiPlayground(useLegacyApiPlayground);
                updateStoreApiEntity(
                    {
                        useLegacyApiPlayground,
                    },
                    apiPlaygroundStoreKey,
                    activeApiEntityId
                );
            })
            .catch((e: Response) => {
                setUiState({
                    isLoading: false,
                    isError: true,
                    statusCode: e.status,
                    message: e.statusText,
                });
                setUseLegacyApiPlayground(false);
                updateStoreApiEntity(
                    {
                        useLegacyApiPlayground: false,
                    },
                    apiPlaygroundStoreKey,
                    activeApiEntityId
                );
            });
    }, [activeApiEntityId]);

    useUpdateEffect(() => {
        if (headerState.isSaveClicked) {
            saveData();
            resetHeaderState();
        }
    }, [headerState]);

    const saveData = () => {
        setUiState(utilityFunctions.getSavingUiState());
        const promise = updateApiPlaygroundOptions(activeApiEntityId, {
            apiPlaygroundOptions: { useLegacyApiPlayground: useLegacyApiPlayground },
        });
        promise
            .then((r) => {
                addNotification({
                    show: true,
                    type: 'success',
                    message: 'API Playground settings saved successfully.',
                });
                updateStoreApiEntity(
                    {
                        useLegacyApiPlayground: useLegacyApiPlayground,
                    },
                    apiPlaygroundStoreKey,
                    activeApiEntityId
                );
            })
            .catch((e) => {
                addNotification({
                    show: true,
                    type: 'error',
                    message: 'Failed to save API Playground settings.',
                });
            })
            .finally(() => {
                setUiState(utilityFunctions.getDefaultUiState());
            });
    };

    useConfirmUnsavedChanges(
        discard,
        {
            useLegacyApiPlayground: useLegacyApiPlayground,
        },
        'apiEntities',
        () => {
            setShowModal(true);
        },
        apiPlaygroundStoreKey,
        activeApiEntityId
    );

    const saveChanges = () => {
        setShowModal(false);
        saveData();
    };

    const discardChanges = () => {
        setShowModal(false);
        setDiscard(true);
    };

    const renderConsoleCallsView = () => {
        return (
            <>
                <NotificationBar />
                <ConfirmUnsavedChanges
                    show={showModal}
                    closeModal={setShowModal}
                    discardChanges={discardChanges}
                    saveChanges={saveChanges}
                />
                <SaveAction
                    disabled={uiState.isLoading}
                    isSaving={uiState.isSaving!}
                    onClick={saveChanges}
                />
                <div className="p-8 pt-0 fade-in-animation">
                    <H4 className="mt-10 mb-3">API Playground</H4>
                    <SubHeading text="Manage settings for API Playground made from the portal."></SubHeading>
                    <Card>
                        <div className="flex flex-col fade-in-animation">
                            <div>
                                <Tooltip
                                    message="Toggle to enable the legacy API playground."
                                    position="right"
                                >
                                    <ToggleSwitch
                                        className="mb-8 fade-in-animation toggle-switch-api-call"
                                        label="Use Legacy API Playground"
                                        checked={useLegacyApiPlayground}
                                        onChange={(e) =>
                                            setUseLegacyApiPlayground(e.target.checked)
                                        }
                                    />
                                </Tooltip>
                            </div>
                        </div>
                    </Card>
                </div>
            </>
        );
    };

    const renderLoadingView = () => {
        return (
            <div className="flex items-center justify-center w-full h-full">
                <Spinner size="xl" />
            </div>
        );
    };
    const renderErrorView = () => {
        return (
            <div className="flex items-center justify-center w-full h-full">
                <p className="text-base font-bold text-red ">
                    {uiState.message
                        ? `${uiState.statusCode} - ${uiState.message}`
                        : 'Oops! Something went wrong.'}
                </p>
            </div>
        );
    };

    return (
        <Fragment>
            {uiState.isLoading
                ? renderLoadingView()
                : uiState.isError
                ? renderErrorView()
                : renderConsoleCallsView()}
        </Fragment>
    );
};

export default ApiPlaygroundView;
